// ─────────────────────────────────────────
// ts/mail.ts — Contact mail delivery helper
// Sends the contact form payload to a backend mail service.
// Tauvior Landing Page
// ─────────────────────────────────────────

import { FORM_ENDPOINT, IS_DEV } from './env';

export interface MailPayload {
  name: string;
  email: string;
  company: string;
  /** Rótulo do assunto escolhido (Tauvior Grid, Advisory…) — pode vir vazio */
  interest: string;
  message: string;
  locale: string;
  source: string;
  recaptchaToken: string;
}

/** O assunto vai na frente para a caixa de entrada já separar por oferta. */
function buildSubject(locale: string, name: string, interest: string): string {
  const prefix = interest ? `[${interest}] ` : '';

  if (locale === 'en-US') {
    return `${prefix}Website contact from ${name}`;
  }

  return `${prefix}Contato pelo site de ${name}`;
}

function buildTextBody(payload: MailPayload): string {
  const labels = payload.locale === 'en-US'
    ? {
        intro: 'A new contact request was submitted through the website.',
        name: 'Name',
        email: 'Email',
        company: 'Company',
        interest: 'Subject',
        locale: 'Locale',
        source: 'Source',
        message: 'Message',
        notProvided: 'Not provided',
      }
    : {
        intro: 'Uma nova solicitacao de contato foi enviada pelo site.',
        name: 'Nome',
        email: 'E-mail',
        company: 'Empresa',
        interest: 'Assunto',
        locale: 'Idioma',
        source: 'Origem',
        message: 'Mensagem',
        notProvided: 'Nao informado',
      };

  const company = payload.company || labels.notProvided;

  return [
    labels.intro,
    '',
    `${labels.name}: ${payload.name}`,
    `${labels.email}: ${payload.email}`,
    `${labels.company}: ${company}`,
    `${labels.interest}: ${payload.interest || labels.notProvided}`,
    `${labels.locale}: ${payload.locale}`,
    `${labels.source}: ${payload.source}`,
    '',
    `${labels.message}:`,
    payload.message,
  ].join('\n');
}

function buildRequestBody(payload: MailPayload): string {
  return JSON.stringify({
    name: payload.name,
    email: payload.email,
    company: payload.company || '',
    interest: payload.interest || '',
    message: payload.message,
    locale: payload.locale,
    source: payload.source,
    'g-recaptcha-response': payload.recaptchaToken,
    _subject: buildSubject(payload.locale, payload.name, payload.interest),
    _replyto: payload.email,
    details: buildTextBody(payload),
  });
}

async function readErrorMessage(response: Response): Promise<string> {
  const contentType = response.headers.get('content-type') ?? '';

  if (contentType.includes('application/json')) {
    const data = await response.json() as {
      error?: string;
      errors?: Array<{ message?: string }>;
    };

    if (typeof data.error === 'string' && data.error.trim()) {
      return data.error.trim();
    }

    const firstMessage = data.errors?.find((item) => item.message?.trim())?.message;
    if (firstMessage) {
      return firstMessage.trim();
    }
  } else {
    const text = (await response.text()).trim();
    if (text) return text;
  }

  return `HTTP ${response.status}: ${response.statusText}`;
}

export async function sendContactMail(payload: MailPayload): Promise<void> {
  if (!FORM_ENDPOINT) {
    if (IS_DEV) {
      console.info('[Contact] Mock submission — payload:', payload);
      return;
    }

    throw new Error('FORM_ENDPOINT is not configured.');
  }

  const response = await fetch(FORM_ENDPOINT, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
    },
    body: buildRequestBody(payload),
  });

  if (!response.ok) {
    const message = await readErrorMessage(response);
    throw new Error(message);
  }
}
